import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { message } from 'antd';
import type {
  LookupTabData,
  AssociationMap,
  TransferModalState,
} from '~/app/components/knowledgeBase/componentSchemaManager/ComponentSchemaManager.types';
import { ComponentSchemaService } from '~/app/services/knowledgeBase';
import type { LookupQueryResult } from './useLookupQueries';

// ── Hook ────────────────────────────────────────────────────────────

export const useAssociations = (
  subsystemData: LookupQueryResult | undefined,
  assetTypeData: LookupQueryResult | undefined,
  componentTypeData: LookupQueryResult | undefined,
  _lookups: Record<string, LookupTabData>,
  setLocalLookupOverrides: React.Dispatch<
    React.SetStateAction<Record<string, LookupTabData>>
  >,
  onRefresh: (configName: string) => void,
) => {
  const { t } = useTranslation('componentSchema');

  // ── Base associations derived from query data (no effect needed) ──

  const baseAssociations = useMemo<Record<string, AssociationMap>>(() => {
    const nextMap: Record<string, AssociationMap> = {
      'Asset Type': {},
      Subsystem: {},
      SubsystemSecondary: {},
      'Component Class': {},
      'Component Type': {},
      'Asset-Subsystem-ComponentClass Mapping': {},
      'Asset-Subsystem-ComponentClass-ComponentType Mapping': {},
    };

    const subsystemRows = subsystemData?.rows || [];
    for (const r of subsystemRows) {
      const assetTypeIds = Array.isArray(r.assetTypeIds)
        ? r.assetTypeIds
        : r.assetTypeIds
          ? [r.assetTypeIds]
          : [];
      (nextMap['Subsystem'] as Record<string, string[]>)[r.code as string] =
        Array.isArray(assetTypeIds) ? assetTypeIds : [];

      const componentClassIds = Array.isArray(r.componentClassIds)
        ? r.componentClassIds
        : r.componentClassIds
          ? [r.componentClassIds]
          : [];
      (nextMap['SubsystemSecondary'] as Record<string, string[]>)[
        r.code as string
      ] = Array.isArray(componentClassIds) ? componentClassIds : [];
    }

    const assetTypeRows = assetTypeData?.rows || [];
    for (const r of assetTypeRows) {
      const ids = Array.isArray(r.subsystemIds)
        ? r.subsystemIds
        : r.subsystemIds
          ? [r.subsystemIds]
          : [];
      (nextMap['Asset Type'] as Record<string, string[]>)[r.code as string] =
        Array.isArray(ids) ? ids : [];
    }

    const componentRows = componentTypeData?.rows || [];
    for (const r of componentRows) {
      const ids = Array.isArray(r.subsystemIds)
        ? r.subsystemIds
        : r.subsystemIds
          ? [r.subsystemIds]
          : [];
      (nextMap['Component Class'] as Record<string, string[]>)[
        r.code as string
      ] = Array.isArray(ids) ? ids : [];
    }

    return nextMap;
  }, [subsystemData?.rows, assetTypeData?.rows, componentTypeData?.rows]);

  // Overrides from transfer modal open / save
  const [associationOverrides, setAssociationOverrides] = useState<
    Record<string, AssociationMap>
  >({});

  // Merge base + overrides
  const associations = useMemo<Record<string, AssociationMap>>(() => {
    const merged: Record<string, AssociationMap> = { ...baseAssociations };
    for (const [key, overrideMap] of Object.entries(associationOverrides)) {
      merged[key] = { ...(merged[key] || {}), ...overrideMap };
    }
    return merged;
  }, [baseAssociations, associationOverrides]);

  const [transferModal, setTransferModal] = useState<TransferModalState>({
    open: false,
    sourceConfigName: '',
    sourceId: '',
    sourceCode: '',
    sourceName: '',
  });

  // ── Transfer modal handlers ─────────────────────────────────────

  const handleOpenTransfer = useCallback(
    async (
      configName: string,
      id: string,
      code: string,
      name: string,
      targetConfigName?: string,
    ) => {
      if (
        configName === 'Asset Type' &&
        (!targetConfigName || targetConfigName === 'Subsystem')
      ) {
        try {
          const [subsystemsRes, includedIds] = await Promise.all([
            ComponentSchemaService.getLookupWithMeta('Subsystem', {
              page: 1,
              limit: 1000,
            }),
            ComponentSchemaService.getSubsystemIdsForAssetType(id),
          ]);

          setLocalLookupOverrides((prev) => ({
            ...prev,
            Subsystem: {
              rows: subsystemsRes.rows,
              loading: false,
              error: null,
            },
          }));

          setAssociationOverrides((prev) => ({
            ...prev,
            'Asset Type': {
              ...(prev['Asset Type'] || {}),
              [code]: includedIds,
            },
          }));

          setTransferModal({
            open: true,
            sourceConfigName: configName,
            sourceId: id,
            sourceCode: code,
            sourceName: name,
            targetConfigName: targetConfigName || 'Subsystem',
          });
        } catch (error) {
          console.error(
            'Failed to load subsystems for asset type modal:',
            error,
          );
          message.error('Failed to load subsystems');
        }
      } else if (
        configName === 'Subsystem' &&
        (!targetConfigName || targetConfigName === 'Asset Type')
      ) {
        try {
          const [assetTypesRes, includedIds] = await Promise.all([
            ComponentSchemaService.getLookupWithMeta('Asset Type', {
              page: 1,
              limit: 1000,
            }),
            ComponentSchemaService.getAssetTypeIdsForSubsystem(id),
          ]);

          setLocalLookupOverrides((prev) => ({
            ...prev,
            'Asset Type': {
              rows: assetTypesRes.rows,
              loading: false,
              error: null,
            },
          }));

          setAssociationOverrides((prev) => ({
            ...prev,
            Subsystem: {
              ...(prev.Subsystem || {}),
              [code]: includedIds,
            },
          }));

          setTransferModal({
            open: true,
            sourceConfigName: configName,
            sourceId: id,
            sourceCode: code,
            sourceName: name,
            targetConfigName: targetConfigName || 'Asset Type',
          });
        } catch (error) {
          console.error(
            'Failed to load asset types for subsystem modal:',
            error,
          );
          message.error('Failed to load asset types');
        }
      } else if (
        configName === 'Asset-Subsystem-ComponentClass Mapping' &&
        targetConfigName === 'Component Class'
      ) {
        try {
          const notIncludedRes =
            await ComponentSchemaService.getComponentClassesForAssetSubsystem(
              id,
              'NOT_INCLUDED',
              1,
              1000,
            );
          const includedRes =
            await ComponentSchemaService.getComponentClassesForAssetSubsystem(
              id,
              'INCLUDED',
              1,
              1000,
            );

          const allComponentClasses = [
            ...includedRes.rows,
            ...notIncludedRes.rows,
          ];
          const includedIds = includedRes.rows.map((r) => r._key);

          setLocalLookupOverrides((prev) => ({
            ...prev,
            'Component Class': {
              rows: allComponentClasses,
              loading: false,
              error: null,
            },
          }));

          setAssociationOverrides((prev) => ({
            ...prev,
            'Asset-Subsystem-ComponentClass Mapping': {
              ...(prev['Asset-Subsystem-ComponentClass Mapping'] || {}),
              [code]: includedIds,
            },
          }));

          setTransferModal({
            open: true,
            sourceConfigName: configName,
            sourceId: id,
            sourceCode: code,
            sourceName: name,
            targetConfigName,
          });
        } catch (error) {
          console.error(
            'Failed to load component classes for mapping modal:',
            error,
          );
          message.error('Failed to load component classes');
        }
      } else if (
        configName === 'Asset-Subsystem-ComponentClass-ComponentType Mapping' &&
        targetConfigName === 'Component Type'
      ) {
        try {
          const notIncludedRes =
            await ComponentSchemaService.getComponentTypesForAssetSubsystemComponentClass(
              id,
              'NOT_INCLUDED',
              1,
              1000,
            );
          const includedRes =
            await ComponentSchemaService.getComponentTypesForAssetSubsystemComponentClass(
              id,
              'INCLUDED',
              1,
              1000,
            );

          const allComponentTypes = [
            ...includedRes.rows,
            ...notIncludedRes.rows,
          ];
          const includedIds = includedRes.rows.map((r) => r._key);

          setLocalLookupOverrides((prev) => ({
            ...prev,
            'Component Type': {
              rows: allComponentTypes,
              loading: false,
              error: null,
            },
          }));

          setAssociationOverrides((prev) => ({
            ...prev,
            'Asset-Subsystem-ComponentClass-ComponentType Mapping': {
              ...(prev[
                'Asset-Subsystem-ComponentClass-ComponentType Mapping'
              ] || {}),
              [code]: includedIds,
            },
          }));

          setTransferModal({
            open: true,
            sourceConfigName: configName,
            sourceId: id,
            sourceCode: code,
            sourceName: name,
            targetConfigName,
          });
        } catch (error) {
          console.error(
            'Failed to load component types for mapping modal:',
            error,
          );
          message.error('Failed to load component types');
        }
      } else {
        setTransferModal({
          open: true,
          sourceConfigName: configName,
          sourceId: id,
          sourceCode: code,
          sourceName: name,
          targetConfigName,
        });
      }
    },
    [setLocalLookupOverrides],
  );

  const handleCloseTransfer = useCallback(() => {
    setTransferModal({
      open: false,
      sourceConfigName: '',
      sourceId: '',
      sourceCode: '',
      sourceName: '',
      targetConfigName: undefined,
    });
  }, []);

  const handleSaveTransfer = useCallback(
    async (targetKeys: string[]) => {
      const { sourceConfigName, sourceId, sourceCode, targetConfigName } =
        transferModal;
      if (!sourceConfigName || !sourceCode || !sourceId) return;

      const counterpartConfig = targetConfigName
        ? targetConfigName
        : sourceConfigName === 'Asset Type'
          ? 'Subsystem'
          : 'Asset Type';

      try {
        if (
          sourceConfigName === 'Asset Type' &&
          counterpartConfig === 'Subsystem'
        ) {
          await ComponentSchemaService.setSubsystemsForAssetType(
            sourceId,
            targetKeys,
          );
        } else if (
          sourceConfigName === 'Subsystem' &&
          counterpartConfig === 'Asset Type'
        ) {
          await ComponentSchemaService.setAssetTypesForSubsystem(
            sourceId,
            targetKeys,
          );
        } else if (
          sourceConfigName === 'Subsystem' &&
          counterpartConfig === 'Component Class'
        ) {
          await ComponentSchemaService.setComponentClassesForSubsystem(
            sourceId,
            targetKeys,
          );
        } else if (
          sourceConfigName === 'Component Class' &&
          counterpartConfig === 'Subsystem'
        ) {
          await ComponentSchemaService.setSubsystemsForComponentClass(
            sourceId,
            targetKeys,
          );
        } else if (
          sourceConfigName === 'Asset-Subsystem-ComponentClass Mapping' &&
          counterpartConfig === 'Component Class'
        ) {
          await ComponentSchemaService.setComponentClassesForAssetSubsystemMapping(
            sourceId,
            targetKeys,
          );
        } else if (
          sourceConfigName ===
            'Asset-Subsystem-ComponentClass-ComponentType Mapping' &&
          counterpartConfig === 'Component Type'
        ) {
          await ComponentSchemaService.setComponentTypesForAssetSubsystemComponentClassMapping(
            sourceId,
            targetKeys,
          );
        } else if (counterpartConfig === 'Component Class') {
          await ComponentSchemaService.setComponentClassesForJunction(
            sourceId,
            targetKeys,
          );
        }

        setAssociationOverrides((prev) => {
          const next = { ...prev };
          const mapKey =
            sourceConfigName === 'Subsystem' &&
            counterpartConfig === 'Component Class'
              ? 'SubsystemSecondary'
              : sourceConfigName === 'Component Class' &&
                  counterpartConfig === 'Subsystem'
                ? 'Component Class'
                : sourceConfigName;

          next[mapKey] = {
            ...(next[mapKey] || {}),
            [sourceCode]: targetKeys,
          };

          return next;
        });

        message.success(t('associations.saved'));
        handleCloseTransfer();

        onRefresh(sourceConfigName);
        onRefresh(counterpartConfig);
      } catch (error) {
        const apiMessage =
          error &&
          typeof error === 'object' &&
          'response' in error &&
          error.response &&
          typeof error.response === 'object' &&
          'data' in error.response &&
          error.response.data &&
          typeof error.response.data === 'object' &&
          'message' in error.response.data &&
          typeof error.response.data.message === 'string'
            ? error.response.data.message
            : null;
        message.error(apiMessage || t('associations.saveFailed'));
      }
    },
    [transferModal, handleCloseTransfer, onRefresh, t],
  );

  return {
    associations,
    transferModal,
    onOpenTransfer: handleOpenTransfer,
    onCloseTransfer: handleCloseTransfer,
    onSaveTransfer: handleSaveTransfer,
  };
};
